import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'components/Dropdown2';
import { CharactersRemaining } from 'components/Form';
import { Button, Form, Input } from 'components/HookForm';
import Message from 'components/Message';
import { createErrorSnackbar, createSuccessSnackbar } from 'components/Snackbar';
import When from 'components/When';
import { ICONS } from 'constants/icons';
import { bem } from 'helpers/class-name';
import { getApiErrorSnackbarDescription } from 'helpers/api-error-snackbar.helpers';
import { reportError } from 'helpers/error-logging';
import Segment from 'helpers/segment';
import { selectActiveVenue } from 'selectors/general';

import { SKU_NAME_CHAR_COUNTER_THRESHOLD, SKU_NAME_MAX_INPUT_LENGTH } from '../SkuItems.constants';
import { createSkuSubgroup, linkItemToSubgroup } from '../SkuItems.helpers';
import { isVisibleSkuEntity } from '../SkuMenuData.normalizers';
import { getSkuVenueSource } from '../SkuVenueSource.helpers';
import { SKU_ANALYTICS_EVENTS } from '../SkuAnalytics.constants';
import messages from './DuplicateCategoryModal.messages';
import './DuplicateCategoryModal.scss';

const { block, element } = bem('duplicate-category-modal');

export const DUPLICATE_CATEGORY_MODAL_NAME = 'DuplicateCategoryModal';

const createDuplicatedCategory = (venueId, menuId, name) =>
  createSkuSubgroup(venueId, { groupId: menuId, name, enabled: false });

const linkItemsToCategory = async (venueId, categoryId, items, source) => {
  const results = await Promise.allSettled(
    items.map((item, ordinal) => linkItemToSubgroup(venueId, categoryId, item.id, ordinal))
  );
  items.forEach((item, idx) => {
    if (results[idx].status === 'fulfilled') {
      Segment.event(SKU_ANALYTICS_EVENTS.CATEGORY_ITEM_ADDED, { venueId, categoryId, itemId: item.id, source });
    }
  });
  return results.filter(r => r.status === 'rejected').length;
};

const showDuplicateResultSnackbar = (dispatch, name, failedCount) => {
  if (failedCount > 0) {
    reportError('Some items failed to link to duplicated SKU category', new Error(`${failedCount} link failures`));
    dispatch(
      createErrorSnackbar(
        messages.categoryDuplicatedWithLinkErrors,
        { categoryName: name, failedCount, strong: chunks => <strong>{chunks}</strong> },
        { isLowStyle: true }
      )
    );
  } else {
    dispatch(
      createSuccessSnackbar(
        messages.categoryDuplicated,
        { categoryName: name, strong: chunks => <strong>{chunks}</strong> },
        { isLowStyle: true }
      )
    );
  }
};

const submitDuplicate = async ({
  name,
  venueId,
  sourceCategoryId,
  selectedMenu,
  itemsToLink,
  source,
  dispatch,
  onSuccess,
  closeModal,
}) => {
  let createResponse;
  try {
    createResponse = await createDuplicatedCategory(venueId, selectedMenu.id, name);
  } catch (error) {
    reportError('Error creating SKU category during duplicate', error);
    dispatch(
      createErrorSnackbar(
        messages.categoryDuplicateError,
        { strong: chunks => <strong>{chunks}</strong> },
        { isLowStyle: true },
        getApiErrorSnackbarDescription(error)
      )
    );
    return;
  }

  const newCategoryId = createResponse?.subGroup?.id || createResponse?.id;
  Segment.event(SKU_ANALYTICS_EVENTS.CATEGORY_DUPLICATED, {
    venueId,
    categoryId: newCategoryId,
    sourceCategoryId,
    source,
  });
  const failedCount = await linkItemsToCategory(venueId, newCategoryId, itemsToLink, source);
  showDuplicateResultSnackbar(dispatch, name, failedCount);

  try {
    await onSuccess?.();
  } catch (error) {
    reportError('Error refreshing after category duplicate', error);
  }
  closeModal();
};

const DuplicateCategoryModal = ({ category, menus, venueId, onSuccess, closeModal }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const venue = useSelector(selectActiveVenue);
  const source = getSkuVenueSource(venue);

  const enabledMenus = useMemo(() => (menus || []).filter(isVisibleSkuEntity), [menus]);
  const sourceMenu = useMemo(
    () => enabledMenus.find(menu => String(menu.id) === String(category.menuId)) || null,
    [enabledMenus, category.menuId]
  );
  const [selectedMenu, setSelectedMenu] = useState(sourceMenu);

  const sourceItems = category.items || [];
  const [removedItemIds, setRemovedItemIds] = useState(() => new Set());
  const visibleItems = useMemo(
    () => sourceItems.filter(item => !removedItemIds.has(item.id)),
    [sourceItems, removedItemIds]
  );

  const defaultCategoryName = useMemo(
    () => intl.formatMessage(messages.copySuffix, { categoryName: category.name }),
    [intl, category.name]
  );

  const handleRemoveItem = itemId => {
    setRemovedItemIds(prev => {
      const next = new Set(prev);
      next.add(itemId);
      return next;
    });
  };

  return (
    <div className={block()}>
      <header className={element('header')}>
        <h3 className={element('title')}>
          <Message message={messages.title} />
        </h3>
        <button
          type="button"
          aria-label={intl.formatMessage(messages.closeAriaLabel)}
          className={element('close-icon')}
          onClick={closeModal}>
          <Isvg src={ICONS.cross} />
        </button>
      </header>
      <Form id="duplicate-category-form" defaultValues={{ categoryName: defaultCategoryName }}>
        {({ formState, handleSubmit, watch }) => {
          const { isSubmitting } = formState;
          const watchedName = watch('categoryName') || '';
          const trimmedName = watchedName.trim();
          const canSubmit = Boolean(trimmedName) && Boolean(selectedMenu);

          const handleFormSubmit = () =>
            submitDuplicate({
              name: trimmedName,
              venueId,
              sourceCategoryId: category.id,
              selectedMenu,
              itemsToLink: visibleItems,
              source,
              dispatch,
              onSuccess,
              closeModal,
            });

          return (
            <>
              <div className={element('form-fields')}>
                <Input
                  name="categoryName"
                  disabled={isSubmitting}
                  label={messages.categoryNameLabel}
                  placeholder={messages.categoryNamePlaceholder}
                  type="text"
                  maxLength={SKU_NAME_MAX_INPUT_LENGTH}
                  required
                  showRequiredLabel
                  validate={{
                    message: messages.categoryNameRequired,
                    value: value => Boolean(value?.trim()),
                  }}
                />
                <When predicate={SKU_NAME_MAX_INPUT_LENGTH - watchedName.length <= SKU_NAME_CHAR_COUNTER_THRESHOLD}>
                  <div
                    className={element('characters-remaining', {
                      error: watchedName.length >= SKU_NAME_MAX_INPUT_LENGTH,
                    })}>
                    <CharactersRemaining length={SKU_NAME_MAX_INPUT_LENGTH} value={watchedName} showMaxLength />
                  </div>
                </When>
                <div className={element('menu-section')} data-testid="menu-section">
                  <div className={element('menu-label')}>
                    <Message message={messages.selectMenu} />
                    <Message as="span" className={element('required-label')} message={messages.required} />
                  </div>
                  <Dropdown contentClassName={element('dropdown-content')}>
                    {() => (
                      <>
                        <DropdownToggle>
                          <span className={element(selectedMenu ? 'dropdown-value' : 'dropdown-placeholder')}>
                            {selectedMenu ? selectedMenu.name : intl.formatMessage(messages.selectMenu)}
                          </span>
                        </DropdownToggle>
                        <DropdownMenu>
                          {enabledMenus.map(menu => (
                            <DropdownItem
                              key={menu.id}
                              isSelected={selectedMenu?.id === menu.id}
                              onClick={() => setSelectedMenu(menu)}>
                              <span className={element('dropdown-item-name')}>{menu.name}</span>
                            </DropdownItem>
                          ))}
                        </DropdownMenu>
                      </>
                    )}
                  </Dropdown>
                </div>
                {sourceItems.length > 0 && (
                  <div className={element('items-section')} data-testid="items-section">
                    <Message as="div" className={element('items-label')} message={messages.itemsLabel} />
                    <ul className={element('items-list')}>
                      {visibleItems.map(item => (
                        <li key={item.id} className={element('item-row')}>
                          <button
                            type="button"
                            className={element('remove-item-btn')}
                            aria-label={intl.formatMessage(messages.removeItemAriaLabel, { itemName: item.name })}
                            onClick={() => handleRemoveItem(item.id)}>
                            <Isvg src={ICONS.cross} />
                          </button>
                          <span className={element('item-name')}>{item.name}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <footer className={element('footer')}>
                <Message
                  as={Button}
                  className={element('cancel-btn')}
                  disabled={isSubmitting}
                  onClick={closeModal}
                  message={messages.cancel}
                  styled="primary-outline"
                  type={Button.TYPES.BUTTON}
                />
                <Message
                  as={Button}
                  className={element('save-btn')}
                  disabled={!canSubmit || isSubmitting}
                  onClick={handleSubmit(handleFormSubmit)}
                  message={messages.save}
                  styled="primary"
                  type={Button.TYPES.SUBMIT}
                />
              </footer>
            </>
          );
        }}
      </Form>
    </div>
  );
};

DuplicateCategoryModal.propTypes = {
  category: PropTypes.shape({
    id: PropTypes.string.isRequired,
    menuId: PropTypes.string,
    name: PropTypes.string,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string,
      })
    ),
  }).isRequired,
  menus: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      enabled: PropTypes.bool,
    })
  ),
  venueId: PropTypes.number.isRequired,
  onSuccess: PropTypes.func,
  closeModal: PropTypes.func.isRequired,
};

export default DuplicateCategoryModal;
